/**
 * @file 本だなの資料の表紙を出す。
 *
 * @what: ISBN を持つ資料は市販の本の書影 (api/bookCover.ts の coverUrl。教材・よむ の表紙と
 *   同じ出どころ) を出し、書影が無い・ISBN が無い資料は表紙の縮小画像 (cover_path)、それも無ければ
 *   先頭ページの画像 (useSourceCover) を出す。どちらも無い・読み込み中は頭文字の枠を出す。別冊 (解答編) は本体 (問題編) を
 *   parent で渡すと、本体の表紙を土台にして黒を重ね、白字で「解答編」と出す。
 * @why: 先頭ページは取り込んだスキャンの1枚目で、表紙ではなく扉や目次のことがあり、
 *   棚で見分けにくい。市販の本は書影の方が本そのものに見える (代表の要望、2026-09-08)。別冊
 *   (解答例など) は ISBN を持てない決まり (backend の検証) なので先頭ページのままだったが、
 *   本体の表紙に黒を重ねた形にすると、どの本の解答かが表紙だけで分かる (代表の要望、
 *   2026-09-11)。書影を先に試し、引けたときはページ一覧を引かない (資料の数だけ JSON を
 *   取りに行かない)。
 * @failure: Amazon の書影は無いとき1×1の画像が返るので、読み込み後の大きさで判定して
 *   先頭ページに切り替える (MaterialCover と同じ判定)。本体の表紙も先頭ページも引けない
 *   ときは黒を重ねず、別冊自身の題名の頭文字の枠に戻す (頭文字が黒に沈むためである)。
 */

import { useState } from "react";
import { coverUrl } from "../api/bookCover";
import type { StudySource } from "../api/sources";
import { useSourceCover } from "../hooks/useSourceCover";

/**
 * 別冊の表紙に重ねる文字である。
 *
 * @why: 本だなの別冊は「解答編」「解答例」「別冊…の解答」と本ごとに言い方が違うが、表紙は
 *   1語で揃える (代表の判断、2026-09-11)。正確な呼び名は表紙の下に出る題名で分かる。
 */
const COMPANION_COVER_LABEL = "解答編";

export function SourceCover({
  familyId,
  source,
  parent,
  className = "study-shelf-cover",
}: {
  familyId: string;
  source: StudySource;
  /** この資料が別冊として付く本体 (問題編)。渡すと本体の表紙を土台にする。 */
  parent?: StudySource;
  /** 枠の class。既定は本だなのカードの大きさ。 */
  className?: string;
}) {
  // 別冊は本体の表紙を土台にする。本体を渡していない資料は今までどおり自分の表紙を出す
  const isCompanion = parent !== undefined && parent.id === source.companion_of;
  const base = isCompanion ? parent : source;
  const isbnUrl = coverUrl(base.isbn);
  const [isbnFailed, setIsbnFailed] = useState(false);
  const useIsbn = isbnUrl !== null && !isbnFailed;
  const cover = useSourceCover(familyId, base, !useIsbn);
  const label = isCompanion ? (
    <span className="study-cover-companion" aria-hidden="true">
      {COMPANION_COVER_LABEL}
    </span>
  ) : null;

  if (useIsbn) {
    return (
      <div className={className}>
        <img
          src={isbnUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setIsbnFailed(true)}
          onLoad={(e) => {
            if (e.currentTarget.naturalWidth <= 1) setIsbnFailed(true);
          }}
        />
        {label}
      </div>
    );
  }
  if (cover.status !== "ready") {
    return (
      <div className={`${className} ${className}--empty`} aria-hidden="true">
        <span>{source.title.slice(0, 2)}</span>
      </div>
    );
  }
  return (
    <div className={className}>
      <img src={cover.url} alt="" loading="lazy" />
      {label}
    </div>
  );
}
